import { createAsyncThunk, createSelector } from "@reduxjs/toolkit";

import { mergeBoardsOrDatesAndTasks } from "@common-components/tasks/helpers/elements-factory/elementsFactory";
import { AppThunkDispatch} from "@common/types/common-root-state-type";
import { CommonRootState } from "@common/types/common-root-state-type";
import { Task } from "@common-models/task";
import { TaskColumn } from "@common-models/task-column";
import {
  BoardUserReducerActions,
  TaskColumnOptionReducerActions,
  TaskColumnReducerActions,
  boardUserSelectByQuery,
  boardsSelectByQuery,
  myWorksSelectOneFieldByQuery,
  taskColumnSelectByQuery,
  taskColumnSelectOneFieldByQuery,
  taskColumnSelectOneObjectByQuery,
  taskgroupsReducerActions,
  tasksReducerActions,
  tasksSelectByQuery
} from "@common-reducers/DBServiceThunks";
import { myWorkActions } from "./MyWorkReducer";
import { getMyWork } from "./backend-api/backend-api";

const getMyWorkTaskList = createSelector(
  [
    (state: CommonRootState) => state,
    (state: CommonRootState) => state.MyWorkReducer?.myWorkPreferences,
  ],
  (state: CommonRootState, myWorkPreferences) => {
    if (Object.keys(myWorkPreferences).length > 0) {
      const allTasks: Task[] = [];

      const boardUsers = boardUserSelectByQuery(state.DBBoardUserReducer, {
        userId: { $in: myWorkPreferences?.people },
      });

      const boardUserIds = boardUsers.map((boardUser) =>
        boardUser._id?.toString()
      );

      const personPickerColumns: TaskColumn[] = [];
      const myWorkBoards = getMyWorkBoards(state);

      myWorkBoards.forEach((board) => {
        const boardPersonPickerColumns = taskColumnSelectByQuery(
          state.DBTaskColumnReducer,
          {
            boardId: board._id,
            type: "person-picker",
          }
        );

        if (boardPersonPickerColumns && boardPersonPickerColumns?.length) {
          personPickerColumns.push(...boardPersonPickerColumns);
        }
      });

      personPickerColumns.forEach((column) => {
        const tasks = tasksSelectByQuery(state.DBTasksReducer, {
          [`customData.${column._id}.value.id`]: {
            $in: boardUserIds,
          },
          $or: [{ deleted: { $exists: false } }, { deleted: false }],
        });

        allTasks.push(...tasks);
      });

      const updatedAllTasks = allTasks.map((task) => {
        const datePickerColumnId = taskColumnSelectOneFieldByQuery(
          state.DBTaskColumnReducer,
          {
            boardId: task.boardId,
            type: "datepicker",
          },
          "_id",
          { order: 1 }
        );

        return { ...task, datePickerColumnId };
      });

      return updatedAllTasks.filter(
        (task) =>
          !state.MyWorkReducer?.myWorkPreferences.hiddenBoards?.includes(
            task.boardId ?? ''
          )
      );
    } else {
      return [];
    }
  }
);

export const getMyWorkBoards = createSelector(
  [(state: CommonRootState) => state, (state: CommonRootState) => state.MyWorkReducer?.myWorkBoardsIds],
  (state: CommonRootState, boardsIds) => {
    return boardsSelectByQuery(state.DBBoardsReducer, {
      _id: { $in: boardsIds },
    });
  }
);

//write a selector that get myworkprefernces currentview by id

export const getMyWorkPreferencesCurrentView = createSelector(
  [
    (state: CommonRootState) => state,
  ],
  (state: CommonRootState) => {
    const myWorkPreferencesId = state.MyWorkReducer.myWorkPreferences._id;
    return myWorksSelectOneFieldByQuery(
      state.DBMyWorksReducer,
      {
        _id: myWorkPreferencesId,
      },
      "currentView"
    );
  }
);

export const fetchMyWorkThunk = createAsyncThunk(
  "myWork/fetchWork",
  async (_, thunkAPI) => {
    const dispatch = thunkAPI.dispatch as AppThunkDispatch;
    dispatch(myWorkActions.setMyWorkLoading(true));

    let res = await dispatch(getMyWork.initiate({})).unwrap();

    let tasks = [...res.tasks, ...res.subTasks, ...res.parentTasks];
    dispatch(tasksReducerActions.addMany(tasks));

    dispatch(
      tasksReducerActions.updateMany(
        tasks.map((task) => ({
          id: task._id,
          changes: task,
        }))
      )
    );

    dispatch(TaskColumnReducerActions.addMany(res.columns));
    dispatch(
      TaskColumnReducerActions.updateMany(
        res.columns.map((column: { _id: string }) => ({
          id: column._id,
          changes: column,
        }))
      )
    );

    dispatch(BoardUserReducerActions.addMany(res.boardUsers));
    dispatch(
      BoardUserReducerActions.updateMany(
        res.boardUsers.map((boardUser: { _id: string }) => ({
          id: boardUser._id,
          changes: boardUser,
        }))
      )
    );

    dispatch(taskgroupsReducerActions.addMany(res.groups));
    dispatch(
      taskgroupsReducerActions.updateMany(
        res.groups.map((group: { _id: string }) => ({
          id: group._id,
          changes: group,
        }))
      )
    );
    dispatch(TaskColumnOptionReducerActions.addMany(res.taskColumnOptions));
    dispatch(
      TaskColumnOptionReducerActions.updateMany(
        res.taskColumnOptions.map((columnOption: { _id: string }) => ({
          id: columnOption._id,
          changes: columnOption,
        }))
      )
    );
    // dispatch(myWorkActions.setMyWorkTasks(visibleTasks));
    dispatch(myWorkActions.setMyWorkBoardsIds(res.boardsIds));

    dispatch(myWorkActions.setMyWorkPrefernces(res.myWorkPreferences));

    dispatch(myWorkActions.setMyWorkLoading(false));
    dispatch(
      myWorkActions.setMyWorkViewMode(res.myWorkPreferences.currentView)
    );

    return res;
  }
);

export const selectMyWorkElementList = createSelector(
  (state: CommonRootState) => state,
  (state: CommonRootState) => state?.MyWorkReducer?.myWorkPreferences,
  (state: CommonRootState) => getMyWorkBoards(state),
  (state: CommonRootState) => state?.MyWorkReducer?.myWorkViewMode,
  (state: CommonRootState) => state?.MyWorkReducer?.myWorkTasksFilter,
  (state: CommonRootState, myWorkPreferences, boards, viewMode, tasksFilterText) => {
    let tasks = getMyWorkTaskList(state);
    let elementsList: ReturnType<typeof mergeBoardsOrDatesAndTasks> = [];
    let hideEmptyGroups = false;
    if (tasksFilterText) {
      tasks = tasks?.filter((task) => tasksFilterText?.test(task.text));
      hideEmptyGroups = true;
    }

    const filteredBoards = boards?.filter(
      (board) => board._id !== undefined && !myWorkPreferences.hiddenBoards?.includes(board._id)
    );

    elementsList = mergeBoardsOrDatesAndTasks(
      filteredBoards,
      tasks,
      viewMode,
      hideEmptyGroups,
      myWorkPreferences
    );

    return elementsList;
  }
);

export const getFirstColumnByBoardIdAndType = createSelector(
  [
    (state: CommonRootState, boardId, type) => state,
    (state: CommonRootState, boardId, type) => boardId,
    (state: CommonRootState, boardId, type) => type,
  ],
  (state: CommonRootState, boardId, type) => {
    const firstColumn = taskColumnSelectOneObjectByQuery(
      state.DBTaskColumnReducer,
      {
        boardId: boardId,
        type: type,
      },
      { order: -1 }
    );

    return firstColumn;
  }
);
